import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import A1 from 'assets/A1.svg?react';
import A2 from 'assets/A2.svg?react';
import B1 from 'assets/B1.svg?react';
import B2 from 'assets/B2.svg?react';
import C1 from 'assets/C1.svg?react';
import C2 from 'assets/C2.svg?react';
import UICard from 'components/ui/UICard';
import UIText from 'components/ui/UIText';
import UIFlex from 'components/ui/UIFlex';
import UIBox from 'components/ui/UIBox';
import ProgressBar from 'components/ProgressBar';
import { CategoryTO, WordSetCategoryType } from 'app/types';

interface ICategoryCardProps {
  record: CategoryTO;
  link?: string;
  active?: boolean;
  index: number;
  wordSetType?: WordSetCategoryType;
  /**
   * Accent inherited from the /wordset row this card lives under. Used
   * for the card frame (border, hover bg, eyebrow, CTA, "Поточний"
   * badge) so the inside page reads as the same colour scheme as the
   * /wordset overview row. CEFR levels still get their distinct SVG
   * badges — those carry per-level identity on their own.
   */
  parentAccent?: string;
}

const CEFR_META: Record<
  string,
  { en: string; Icon: React.FC<React.SVGProps<SVGSVGElement>> }
> = {
  A1: { en: 'Beginner', Icon: A1 },
  A2: { en: 'Elementary', Icon: A2 },
  B1: { en: 'Intermediate', Icon: B1 },
  B2: { en: 'Upper-Intermediate', Icon: B2 },
  C1: { en: 'Advanced', Icon: C1 },
  C2: { en: 'Proficiency', Icon: C2 },
};

const DEFAULT_ACCENT = '#3B2C93';

const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(
    h.slice(2, 4),
    16,
  )}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
};

const StatPill: React.FC<{ n?: number | string; u: string }> = ({ n, u }) => (
  <UIBox
    sx={{
      alignItems: 'baseline',
      bgcolor: 'background.default',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 999,
      display: 'inline-flex',
      fontSize: 13,
      gap: 0.6,
      px: 1.4,
      py: 0.6,
      whiteSpace: 'nowrap',
    }}
  >
    <UIText component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
      {n ?? 0}
    </UIText>
    <UIText component={'span'} color={'primary.subtitle'} sx={{ fontSize: 13 }}>
      {u}
    </UIText>
  </UIBox>
);

const CategoryCard: React.FC<ICategoryCardProps> = ({
  record: {
    categoryName,
    title,
    subTitle,
    subCategoryCount,
    totalWords,
    addedWords,
    learnedWords,
  },
  link,
  active = false,
  index,
  wordSetType,
  parentAccent,
}) => {
  const cefr = CEFR_META[categoryName];
  // Single accent drives the whole card frame — comes from the parent
  // /wordset row when available, falls back to the brand default.
  const accent = parentAccent ?? DEFAULT_ACCENT;
  const started = addedWords > 0;
  const isCefrPage =
    wordSetType === 'level-based' || wordSetType === 'irregular-verb';

  const cta = 'Відкрити';

  const withLink = (children: ReactElement) =>
    link ? (
      <Link style={{ display: 'block', width: '100%' }} to={link}>
        {children}
      </Link>
    ) : (
      children
    );

  return withLink(
    <UICard
      sx={{
        bgcolor: active ? hexToRgba(accent, 0.06) : 'background.paper',
        border: '1px solid',
        borderColor: active ? accent : 'divider',
        p: 0,
        transition: 'border-color .16s ease',
        '&:hover': {
          borderColor: accent,
          bgcolor: active ? hexToRgba(accent, 0.08) : hexToRgba(accent, 0.04),
        },
        '&:hover .cc-arrow': {
          transform: 'translateX(4px)',
        },
      }}
    >
      <UIFlex
        ai={'center'}
        sx={{
          flexWrap: 'nowrap',
          gap: { xs: 1.75, md: 2.5 },
          p: { xs: '14px', md: '16px 20px' },
        }}
      >
        {/* Thumbnail */}
        <UIBox
          sx={{
            alignItems: 'center',
            bgcolor: hexToRgba(accent, 0.1),
            borderRadius: 2,
            display: 'flex',
            flex: 'none',
            height: { xs: 60, md: 96 },
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
            width: { xs: 60, md: 96 },
          }}
        >
          {isCefrPage && cefr ? (
            <UIBox
              sx={{
                filter: 'drop-shadow(0 2px 6px rgba(20,20,20,0.16))',
                '& svg': { width: { xs: 36, md: 62 }, height: 'auto' },
              }}
            >
              <cefr.Icon />
            </UIBox>
          ) : (
            <UIText
              sx={{
                color: accent,
                fontSize: { xs: 24, md: 36 },
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {index + 1}
            </UIText>
          )}
        </UIBox>

        {/* Content */}
        <UIBox sx={{ flex: 1, minWidth: 0 }}>
          <UIFlex
            ai={'center'}
            jc={'flex-start'}
            sx={{ flexWrap: 'wrap', gap: 1, mb: 0.5 }}
          >
            <UIText
              sx={{
                // English-side eyebrow (CEFR rank + English level name, or
                // the raw English category key for non-CEFR pages) renders
                // in default text colour so the Ukrainian title below
                // owns the accent. Matches WordSetRow's `en` + `title` rule
                // — keeps the same en-black / ua-accent rhythm everywhere
                // a subcategory card shows both languages.
                color: 'text.primary',
                fontSize: { xs: 10, md: 11 },
                fontWeight: 800,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {isCefrPage && cefr
                ? `${categoryName} · ${cefr.en}`
                : categoryName}
            </UIText>
            {active && (
              <UIBox
                sx={{
                  bgcolor: hexToRgba(accent, 0.16),
                  borderRadius: 999,
                  color: accent,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  px: 1,
                  py: '2px',
                  textTransform: 'uppercase',
                }}
              >
                {'Поточний'}
              </UIBox>
            )}
          </UIFlex>

          <UIText
            variant={'h3'}
            sx={{ color: accent, fontSize: { xs: 17, md: 21 }, mb: 0.6 }}
          >
            {title}
          </UIText>
          <UIText
            color={'primary.subtitle'}
            sx={{
              display: '-webkit-box',
              fontSize: { xs: 13, md: 14 },
              mb: 1.25,
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: { xs: 2, md: 1 },
            }}
          >
            {subTitle}
          </UIText>
          <UIFlex jc={'flex-start'} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <StatPill n={subCategoryCount} u={'тем'} />
            <StatPill n={totalWords} u={'слів'} />
          </UIFlex>
        </UIBox>

        {/* Right: progress + CTA */}
        <UIBox
          sx={{
            alignItems: 'stretch',
            display: { xs: 'none', md: 'flex' },
            flex: 'none',
            flexDirection: 'column',
            gap: 1.5,
            width: 250,
          }}
        >
          {started && (
            <ProgressBar
              addedValue={addedWords}
              learnedValue={learnedWords}
              totalValue={totalWords}
              minWidth={'100%'}
              maxWidth={'100%'}
              width={'100%'}
            />
          )}

          <UIBox
            sx={{
              alignItems: 'center',
              color: accent,
              display: 'inline-flex',
              fontSize: 15,
              fontWeight: 700,
              gap: 0.5,
              justifyContent: 'flex-end',
            }}
          >
            {cta}
            <UIBox
              className={'cc-arrow'}
              component={'span'}
              sx={{
                display: 'inline-block',
                transition: 'transform .25s cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              {'→'}
            </UIBox>
          </UIBox>
        </UIBox>
      </UIFlex>
    </UICard>,
  );
};

export default CategoryCard;
